import { Link, NavLink } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { useState } from "react";
import { BUSINESS } from "@/lib/constants";
import { track, EVENTS } from "@/lib/analytics";
import { SERVICES } from "@/data/services";
import { LOCATIONS } from "@/data/locations";

/**
 * Cyan circular target mark — restored from the original A1 logo treatment.
 * Concentric rings with a cyan accent, sitting on a deep navy background.
 */
function BrandMark({ size = 36 }) {
    return (
        <svg viewBox="0 0 40 40" width={size} height={size} aria-hidden="true">
            <circle cx="20" cy="20" r="18" stroke="#28d8e0" strokeWidth="2" fill="none" />
            <circle cx="20" cy="20" r="11" stroke="#28d8e0" strokeWidth="1.6" fill="none" opacity="0.85" />
            <circle cx="20" cy="20" r="5" fill="#28d8e0" />
            <circle cx="20" cy="20" r="2.2" fill="#0a1426" />
        </svg>
    );
}

export default function Header() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const [areasOpen, setAreasOpen] = useState(false);

    return (
        <header
            className="sticky top-0 z-30 border-b"
            style={{
                background: "var(--color-bg)",
                borderColor: "var(--color-line)",
            }}
            data-testid="site-header"
        >
            <div className="a1-container flex items-center justify-between py-3 md:py-4">
                <Link
                    to="/"
                    className="flex items-center gap-3 no-underline group"
                    data-testid="header-logo"
                >
                    <BrandMark size={36} />
                    <span className="leading-tight">
                        <span
                            className="block serif text-[15px] md:text-[18px] font-bold tracking-wide"
                            style={{ color: "var(--color-text)" }}
                        >
                            A-1 Screening Services Inc.
                        </span>
                        <span
                            className="block text-[10px] md:text-[11px] font-bold uppercase tracking-[0.2em]"
                            style={{ color: "var(--color-accent)" }}
                        >
                            Naples & SW Florida
                        </span>
                    </span>
                </Link>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-7">
                    <div
                        className="relative"
                        onMouseEnter={() => setServicesOpen(true)}
                        onMouseLeave={() => setServicesOpen(false)}
                    >
                        <button
                            className="text-[13px] font-bold uppercase tracking-wider inline-flex items-center gap-1 hover:text-[var(--color-accent)]"
                            style={{ color: "var(--color-text)" }}
                            data-testid="nav-services"
                        >
                            Services <ChevronDown size={14} />
                        </button>
                        {servicesOpen && (
                            <div className="absolute left-0 top-full pt-2 z-30">
                                <div
                                    className="rounded-md py-2 w-72 border shadow-[var(--shadow-md)]"
                                    style={{ background: "var(--color-bg-card)", borderColor: "var(--color-line)" }}
                                >
                                    {SERVICES.map((s) => (
                                        <NavLink
                                            key={s.slug}
                                            to={`/services/${s.slug}`}
                                            className="block px-4 py-2.5 text-sm no-underline hover:bg-[var(--color-bg-card-2)]"
                                            style={{ color: "var(--color-text)" }}
                                            data-testid={`nav-service-${s.slug}`}
                                        >
                                            {s.name}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div
                        className="relative"
                        onMouseEnter={() => setAreasOpen(true)}
                        onMouseLeave={() => setAreasOpen(false)}
                    >
                        <button
                            className="text-[13px] font-bold uppercase tracking-wider inline-flex items-center gap-1 hover:text-[var(--color-accent)]"
                            style={{ color: "var(--color-text)" }}
                            data-testid="nav-areas"
                        >
                            Service Areas <ChevronDown size={14} />
                        </button>
                        {areasOpen && (
                            <div className="absolute left-0 top-full pt-2 z-30">
                                <div
                                    className="rounded-md py-2 w-60 border shadow-[var(--shadow-md)]"
                                    style={{ background: "var(--color-bg-card)", borderColor: "var(--color-line)" }}
                                >
                                    {LOCATIONS.map((l) => (
                                        <NavLink
                                            key={l.slug}
                                            to={`/service-areas/${l.slug}`}
                                            className={`block px-4 py-2.5 text-sm no-underline hover:bg-[var(--color-bg-card-2)] ${l.is_primary ? "font-bold" : ""}`}
                                            style={{ color: l.is_primary ? "var(--color-accent)" : "var(--color-text)" }}
                                            data-testid={`nav-area-${l.slug}`}
                                        >
                                            {l.name}{l.is_primary ? " · Home Base" : ""}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <NavLink to="/gallery" className="text-[13px] font-bold uppercase tracking-wider no-underline hover:text-[var(--color-accent)]" style={{ color: "var(--color-text)" }} data-testid="nav-gallery">Gallery</NavLink>
                    <NavLink to="/reviews" className="text-[13px] font-bold uppercase tracking-wider no-underline hover:text-[var(--color-accent)]" style={{ color: "var(--color-text)" }} data-testid="nav-reviews">Reviews</NavLink>
                    <NavLink to="/faq" className="text-[13px] font-bold uppercase tracking-wider no-underline hover:text-[var(--color-accent)]" style={{ color: "var(--color-text)" }} data-testid="nav-faq">FAQ</NavLink>
                    <NavLink to="/blog" className="text-[13px] font-bold uppercase tracking-wider no-underline hover:text-[var(--color-accent)]" style={{ color: "var(--color-text)" }} data-testid="nav-blog">Blog</NavLink>
                    <NavLink to="/about" className="text-[13px] font-bold uppercase tracking-wider no-underline hover:text-[var(--color-accent)]" style={{ color: "var(--color-text)" }} data-testid="nav-about">About</NavLink>
                    <a
                        href={`tel:${BUSINESS.phoneTel}`}
                        onClick={() => track(EVENTS.PHONE_CLICK, { placement: "header" })}
                        className="text-[13px] font-bold no-underline inline-flex items-center gap-2"
                        style={{ color: "var(--color-accent)" }}
                        data-testid="header-phone-link"
                    >
                        <Phone size={15} strokeWidth={2.5} /> {BUSINESS.phone}
                    </a>
                    <Link to="/free-estimate" className="a1-btn-primary !min-h-[42px] !py-2.5 !px-5 !text-[13px]" data-testid="header-cta-estimate">
                        Free Estimate
                    </Link>
                </nav>

                {/* Mobile actions */}
                <div className="lg:hidden flex items-center gap-2">
                    <a
                        href={`tel:${BUSINESS.phoneTel}`}
                        onClick={() => track(EVENTS.PHONE_CLICK, { placement: "mobile-header" })}
                        className="inline-flex items-center gap-1.5 text-[13px] font-bold no-underline px-3 py-2 rounded-md uppercase tracking-wider border"
                        style={{
                            color: "var(--color-accent)",
                            borderColor: "var(--color-accent)",
                        }}
                        data-testid="mobile-header-phone"
                    >
                        <Phone size={15} strokeWidth={2.5} /> Call
                    </a>
                    <button
                        type="button"
                        aria-label="Open menu"
                        onClick={() => setMobileOpen(true)}
                        className="p-2 rounded-md"
                        style={{ color: "var(--color-text)" }}
                        data-testid="mobile-menu-toggle"
                    >
                        <Menu size={26} />
                    </button>
                </div>
            </div>

            {/* Mobile overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 z-40 lg:hidden overflow-y-auto"
                    style={{ background: "var(--color-bg)" }}
                    data-testid="mobile-menu-overlay"
                >
                    <div
                        className="a1-container py-3 flex justify-between items-center border-b sticky top-0"
                        style={{ background: "var(--color-bg)", borderColor: "var(--color-line)" }}
                    >
                        <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 no-underline">
                            <BrandMark size={32} />
                            <span className="serif text-base font-bold" style={{ color: "var(--color-text)" }}>
                                A-1 Screening
                            </span>
                        </Link>
                        <button
                            type="button"
                            aria-label="Close menu"
                            onClick={() => setMobileOpen(false)}
                            className="p-2 rounded-md"
                            style={{ color: "var(--color-text)" }}
                            data-testid="mobile-menu-close"
                        >
                            <X size={26} />
                        </button>
                    </div>
                    <nav className="a1-container py-5 flex flex-col gap-1">
                        <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] mt-2 mb-1" style={{ color: "var(--color-accent)" }}>
                            Services
                        </h3>
                        {SERVICES.map((s) => (
                            <NavLink
                                key={s.slug}
                                to={`/services/${s.slug}`}
                                onClick={() => setMobileOpen(false)}
                                className="py-2.5 no-underline text-base"
                                style={{ color: "var(--color-text)" }}
                                data-testid={`mobile-nav-service-${s.slug}`}
                            >
                                {s.name}
                            </NavLink>
                        ))}
                        <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] mt-5 mb-1" style={{ color: "var(--color-accent)" }}>
                            Service Areas
                        </h3>
                        {LOCATIONS.map((l) => (
                            <NavLink
                                key={l.slug}
                                to={`/service-areas/${l.slug}`}
                                onClick={() => setMobileOpen(false)}
                                className={`py-2.5 no-underline text-base ${l.is_primary ? "font-bold" : ""}`}
                                style={{ color: l.is_primary ? "var(--color-accent)" : "var(--color-text)" }}
                                data-testid={`mobile-nav-area-${l.slug}`}
                            >
                                {l.name}{l.is_primary ? " · Home Base" : ""}
                            </NavLink>
                        ))}
                        <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] mt-5 mb-1" style={{ color: "var(--color-accent)" }}>
                            Company
                        </h3>
                        <NavLink to="/gallery" onClick={() => setMobileOpen(false)} className="py-2.5 no-underline text-base" style={{ color: "var(--color-text)" }}>Gallery</NavLink>
                        <NavLink to="/reviews" onClick={() => setMobileOpen(false)} className="py-2.5 no-underline text-base" style={{ color: "var(--color-text)" }}>Reviews</NavLink>
                        <NavLink to="/faq" onClick={() => setMobileOpen(false)} className="py-2.5 no-underline text-base" style={{ color: "var(--color-text)" }}>FAQ</NavLink>
                        <NavLink to="/blog" onClick={() => setMobileOpen(false)} className="py-2.5 no-underline text-base" style={{ color: "var(--color-text)" }}>Blog</NavLink>
                        <NavLink to="/about" onClick={() => setMobileOpen(false)} className="py-2.5 no-underline text-base" style={{ color: "var(--color-text)" }}>About</NavLink>
                        <NavLink to="/contact" onClick={() => setMobileOpen(false)} className="py-2.5 no-underline text-base" style={{ color: "var(--color-text)" }}>Contact</NavLink>
                        <Link to="/free-estimate" onClick={() => setMobileOpen(false)} className="a1-btn-primary mt-5 w-full">
                            Get a Free Estimate
                        </Link>
                        <a
                            href={`tel:${BUSINESS.phoneTel}`}
                            onClick={() => setMobileOpen(false)}
                            className="a1-btn-secondary mt-3 w-full"
                        >
                            <Phone size={18} /> Call {BUSINESS.phone}
                        </a>
                    </nav>
                </div>
            )}
        </header>
    );
}
